import { Tooltip } from '../primitives/Tooltip'
import { buildRhythmLegend } from '../../lib/rhythm-legend'

/**
 * Rich tooltip body: a compact legend of the patterns that shape the leave
 * rhythm. Curated UI content from {@link buildRhythmLegend} — plain-language
 * descriptions, no threshold numbers.
 */
function LegendBody(): JSX.Element {
  const items = buildRhythmLegend()
  return (
    <div
      data-testid="rhythm-legend"
      style={{ display: 'flex', flexDirection: 'column', gap: 10, textAlign: 'left' }}
    >
      <div
        style={{
          fontSize: 10,
          fontWeight: 700,
          letterSpacing: '.06em',
          textTransform: 'uppercase',
          opacity: 0.7,
        }}
      >
        What shapes your rhythm
      </div>
      {items.map((it) => (
        <div key={it.label} style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
          <span aria-hidden="true" style={{ fontSize: 13, lineHeight: 1.35, flexShrink: 0 }}>
            {it.icon}
          </span>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 12, fontWeight: 700, lineHeight: 1.35 }}>{it.label}</div>
            <div style={{ fontSize: 11.5, opacity: 0.82, lineHeight: 1.4 }}>{it.description}</div>
            {it.performanceRisk ? (
              <div
                data-testid="rhythm-legend-perf-note"
                style={{ fontSize: 11, opacity: 0.82, lineHeight: 1.4, marginTop: 2 }}
              >
                ⚠️ May be noted in a performance conversation.
              </div>
            ) : null}
          </div>
        </div>
      ))}
    </div>
  )
}

/**
 * A subtle text link ("What shapes your rhythm?") pinned to the bottom of the
 * Leave-rhythm card. Hover or keyboard-focus reveals {@link LegendBody};
 * Escape dismisses it (both handled by the shared {@link Tooltip}). The trigger
 * is a real focusable button styled as a link so it is keyboard-reachable, per
 * the UI/UX standard.
 */
export function RhythmInfoTooltip(): JSX.Element {
  return (
    <Tooltip content={<LegendBody />} placement="top-start" delay={150}>
      <button
        type="button"
        data-testid="rhythm-info-trigger"
        aria-label="What shapes your leave rhythm"
        style={{
          appearance: 'none',
          border: 'none',
          background: 'transparent',
          padding: 0,
          font: 'inherit',
          fontSize: 11.5,
          fontWeight: 600,
          color: 'var(--steel-blue-3)',
          textDecoration: 'underline',
          textDecorationStyle: 'dotted',
          textUnderlineOffset: 2,
          cursor: 'pointer',
        }}
      >
        What shapes your rhythm?
      </button>
    </Tooltip>
  )
}
